import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, CalendarRange, Building2, Globe, Instagram, Linkedin, Paperclip, ClipboardList } from "lucide-react";
import api, { apiError } from "@/lib/api";
import RdvReportModal from "@/components/RdvReportModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export const RDV_TYPES = [
  { id: "exterieur", label: "Lieu extérieur" },
  { id: "telephonique", label: "Téléphonique" },
  { id: "visio", label: "Visio" },
];

const toMin = (hhmm) => { const [h, m] = String(hhmm || "10:00").split(":").map(Number); return h * 60 + (m || 0); };
const toHHMM = (m) => `${String(Math.max(0, Math.floor(m / 60))).padStart(2, "0")}:${String(Math.max(0, m % 60)).padStart(2, "0")}`;
const mondayISO = (isoDate) => {
  const d = new Date(isoDate + "T12:00:00");
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const isGeneva = (a) => ["genève", "geneve", "geneva", "genf"].some((k) => (a || "").toLowerCase().includes(k)) || /\b12\d{2}\b/.test(a || "");

export function computeDefaults(type, address, travelDefault) {
  if (type === "telephonique") return { tb: 0, rdv: 30, ta: 0 };
  if (type === "visio") return { tb: 0, rdv: 60, ta: 0 };
  const t = isGeneva(address) ? 60 : travelDefault;
  return { tb: t, rdv: 60, ta: t };
}

export default function RdvModal({ rdv, prospect, defaultDate, onClose, onSaved, onOpenReport }) {
  const [travelDefault, setTravelDefault] = useState(30);
  useEffect(() => {
    api.get("/settings").then((r) => setTravelDefault(Number(r.data.default_travel_minutes) || 30)).catch(() => {});
  }, []);

  const [form, setForm] = useState(() => (rdv ? {
    title: rdv.title || "", type: rdv.type || "visio",
    date: rdv.date || "", start_time: toHHMM(rdv.start_min ?? 600),
    address: rdv.address || "", visio_link: rdv.visio_link || "",
    contact_name: rdv.contact_name || "", email: rdv.email || "", phone: rdv.phone || "",
    description: rdv.description || "", client_id: rdv.client_id || "",
  } : {
    title: prospect ? `RDV ${prospect.name}` : "", type: "visio",
    date: defaultDate || new Date().toISOString().slice(0, 10), start_time: "10:00",
    address: "", visio_link: "",
    contact_name: prospect?.contact_name || prospect?.name || "",
    email: prospect?.email || "", phone: prospect?.phone || "",
    description: "", client_id: "",
  }));
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {});
  }, []);
  const [dur, setDur] = useState(() => (rdv
    ? { tb: rdv.travel_before_min ?? 0, rdv: rdv.rdv_minutes ?? 60, ta: rdv.travel_after_min ?? 0 }
    : computeDefaults(prospect ? "visio" : "visio", "", 30)));
  const [touched, setTouched] = useState(!!rdv);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!rdv && !touched) setDur(computeDefaults(form.type, form.address, travelDefault));
  }, [travelDefault]); // eslint-disable-line

  const applyType = (t) => {
    setForm((f) => ({ ...f, type: t }));
    if (!touched) setDur(computeDefaults(t, form.address, travelDefault));
  };
  const applyAddress = (v) => {
    setForm((f) => ({ ...f, address: v }));
    if (!touched) setDur(computeDefaults(form.type, v, travelDefault));
  };
  const setDurField = (k) => (e) => { setTouched(true); setDur((d) => ({ ...d, [k]: e.target.value })); };

  const start = toMin(form.start_time);
  const bs = start - (Number(dur.tb) || 0);
  const be = start + (Number(dur.rdv) || 0) + (Number(dur.ta) || 0);
  const isExt = form.type === "exterieur";

  const save = async () => {
    if (!form.title.trim() || !form.date || !form.start_time) { toast.error("Titre, date et heure requis"); return; }
    if (isExt && !form.address.trim()) { toast.error("Indiquez le lieu du RDV extérieur"); return; }
    setSaving(true);
    const payload = {
      ...form,
      travel_before_min: Number(dur.tb) || 0,
      rdv_minutes: Number(dur.rdv) || 0,
      travel_after_min: Number(dur.ta) || 0,
    };
    try {
      if (rdv) {
        await api.patch(`/appointments/${rdv.id}`, payload);
        toast.success("RDV mis à jour — agenda synchronisé");
      } else {
        const { data } = await api.post("/appointments", { ...payload, prospect_id: prospect?.id || null });
        toast.success(prospect
          ? "RDV fixé — prospect en « RDV fixé », agenda bloqué"
          : "RDV créé — bloqué dans l'agenda");
        if (data.whatsapp === "sent") toast.success("WhatsApp de confirmation envoyé au contact");
        else if (data.whatsapp === "not_configured") toast.info("WhatsApp non configuré — renseignez les clés Twilio pour les confirmations automatiques");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce RDV ? Le créneau sera libéré dans l'agenda.")) return;
    await api.delete(`/appointments/${rdv.id}`);
    toast.success("RDV supprimé");
    onSaved(); onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rdv-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {rdv ? rdv.title : prospect ? `Fixer un RDV — ${prospect.name}` : "Nouveau rendez-vous"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Titre *</Label><Input data-testid="rdv-title" className="mt-1.5" value={form.title} onChange={set("title")} placeholder="Ex : Présentation de l'offre" /></div>
            <div>
              <Label>Type de RDV</Label>
              <select data-testid="rdv-type" className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white"
                value={form.type} onChange={(e) => applyType(e.target.value)}>
                {RDV_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Client lié (optionnel)</Label>
              <select data-testid="rdv-client" className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white"
                value={form.client_id} onChange={set("client_id")}>
                <option value="">Aucun</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex items-end pb-0.5">
              <Link to={`/agenda?week=${mondayISO(form.date)}`} data-testid="rdv-check-agenda" target="_blank"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline">
                <CalendarRange className="w-3.5 h-3.5" /> Voir l'agenda (disponibilités)
              </Link>
            </div>
            <div><Label>Date *</Label><Input data-testid="rdv-date" type="date" className="mt-1.5" value={form.date} onChange={set("date")} /></div>
            <div><Label>Heure du RDV *</Label><Input data-testid="rdv-time" type="time" className="mt-1.5" value={form.start_time} onChange={set("start_time")} /></div>
          </div>

          <div className="border border-neutral-200 rounded-lg p-3 space-y-3 bg-neutral-50/50">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Contact</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom du contact</Label><Input data-testid="rdv-contact-name" className="mt-1.5 bg-white" value={form.contact_name} onChange={set("contact_name")} /></div>
              <div><Label>Téléphone (WhatsApp)</Label><Input data-testid="rdv-phone" className="mt-1.5 bg-white" value={form.phone} onChange={set("phone")} placeholder="+41… / 06…" /></div>
            </div>
            <div><Label>Email</Label><Input data-testid="rdv-email" type="email" className="mt-1.5 bg-white" value={form.email} onChange={set("email")} /></div>
          </div>

          <div className="border border-neutral-200 rounded-lg p-3 space-y-3 bg-neutral-50/50" data-testid="rdv-durations">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Temps à bloquer dans l'agenda</p>
            <div className={`grid gap-3 ${isExt ? "grid-cols-3" : "grid-cols-1"}`}>
              {isExt && (
                <div><Label>Trajet aller (min)</Label><Input data-testid="rdv-travel-before" type="number" min="0" className="mt-1.5 bg-white" value={dur.tb} onChange={setDurField("tb")} /></div>
              )}
              <div><Label>{form.type === "visio" ? "Durée visio (min)" : form.type === "telephonique" ? "Durée appel (min)" : "Durée du RDV (min)"}</Label>
                <Input data-testid="rdv-duration" type="number" min="5" step="5" className="mt-1.5 bg-white" value={dur.rdv} onChange={setDurField("rdv")} /></div>
              {isExt && (
                <div><Label>Trajet retour (min)</Label><Input data-testid="rdv-travel-after" type="number" min="0" className="mt-1.5 bg-white" value={dur.ta} onChange={setDurField("ta")} /></div>
              )}
            </div>
            <p className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2" data-testid="rdv-duration-preview">
              Bloc agenda : <strong>{toHHMM(bs)} – {toHHMM(be)}</strong>
              {isExt ? ` (trajet ${dur.tb} min · RDV ${dur.rdv} min · retour ${dur.ta} min)` : ""}
              {isGeneva(form.address) && isExt ? " — Genève détecté" : ""}
            </p>
          </div>

          {isExt && (
            <div>
              <Label>Lieu *</Label>
              <Input data-testid="rdv-address" className="mt-1.5" value={form.address} onChange={(e) => applyAddress(e.target.value)} placeholder="Rue, ville — ex : 12 rue du Rhône, Genève" />
            </div>
          )}
          {form.type === "visio" && (
            <div><Label>Lien visio</Label><Input data-testid="rdv-visio-link" className="mt-1.5" value={form.visio_link} onChange={set("visio_link")} placeholder="https://meet.google.com/…" /></div>
          )}
          <div>
            <Label>Description / ordre du jour</Label>
            <textarea data-testid="rdv-description" rows={3} value={form.description} onChange={set("description")}
              placeholder="Sujets à aborder, documents à préparer…"
              className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white" />
          </div>

          {rdv?.booked_online && (
            <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 space-y-1.5" data-testid="rdv-booking-info">
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">Reçu via le lien de réservation</p>
              {rdv.booking_status === "a_reconfirmer" && (
                <div className="flex items-center justify-between gap-2 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-2" data-testid="rdv-status-banner">
                  <p className="text-xs font-medium text-orange-800">Le client a demandé un report — nouveau créneau à reconfirmer.</p>
                  <button data-testid="rdv-confirm-btn" onClick={async () => {
                    try {
                      await api.patch(`/appointments/${rdv.id}`, { booking_status: "confirme" });
                      toast.success("RDV reconfirmé");
                      onSaved(); onClose();
                    } catch (e) { toast.error(apiError(e)); }
                  }} className="text-[11px] font-semibold bg-orange-600 text-white rounded-md px-2.5 py-1.5 hover:bg-orange-700 transition-colors shrink-0">
                    Reconfirmer
                  </button>
                </div>
              )}
              {rdv.booking_status === "annulation_demandee" && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-2.5 py-2 space-y-1.5" data-testid="rdv-status-banner">
                  <p className="text-xs font-medium text-red-800">Le client souhaite annuler ce RDV.</p>
                  {rdv.cancel_reason && <p className="text-xs text-red-700">Motif : {rdv.cancel_reason}</p>}
                  <div className="flex gap-2 pt-0.5">
                    <button data-testid="rdv-cancel-accept-btn" onClick={async () => {
                      if (!window.confirm("Accepter l'annulation ? Le créneau sera libéré dans l'agenda.")) return;
                      await api.delete(`/appointments/${rdv.id}`);
                      toast.success("RDV annulé — créneau libéré");
                      onSaved(); onClose();
                    }} className="text-[11px] font-semibold bg-red-600 text-white rounded-md px-2.5 py-1.5 hover:bg-red-700 transition-colors">
                      Accepter l'annulation
                    </button>
                    <button data-testid="rdv-keep-btn" onClick={async () => {
                      try {
                        await api.patch(`/appointments/${rdv.id}`, { booking_status: "confirme" });
                        toast.success("RDV maintenu");
                        onSaved(); onClose();
                      } catch (e) { toast.error(apiError(e)); }
                    }} className="text-[11px] font-semibold bg-white border border-neutral-300 rounded-md px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">
                      Maintenir le RDV
                    </button>
                  </div>
                </div>
              )}
              {rdv.company && <p className="text-xs text-neutral-700 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-neutral-400" /> {rdv.company}</p>}
              {rdv.website && (
                <a data-testid="rdv-booking-website" href={/^https?:\/\//.test(rdv.website) ? rdv.website : `https://${rdv.website}`} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-700 hover:underline flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> {rdv.website}</a>
              )}
              {rdv.instagram && (
                <a data-testid="rdv-booking-instagram" href={/^https?:\/\//.test(rdv.instagram) ? rdv.instagram : `https://instagram.com/${rdv.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-700 hover:underline flex items-center gap-1.5"><Instagram className="w-3.5 h-3.5" /> {rdv.instagram}</a>
              )}
              {rdv.linkedin && (
                <a data-testid="rdv-booking-linkedin" href={/^https?:\/\//.test(rdv.linkedin) ? rdv.linkedin : `https://${rdv.linkedin}`} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-700 hover:underline flex items-center gap-1.5"><Linkedin className="w-3.5 h-3.5" /> {rdv.linkedin}</a>
              )}
              {rdv.attachment?.url && (
                <a data-testid="rdv-booking-attachment" href={rdv.attachment.url} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-700 hover:underline flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> {rdv.attachment.name}</a>
              )}
              {rdv.booking_status === "annule" && (
                <div className="bg-neutral-100 border border-neutral-200 rounded-lg px-2.5 py-2 space-y-1" data-testid="rdv-status-banner">
                  <p className="text-xs font-medium text-neutral-700">Le client a annulé ce RDV — le créneau a été libéré dans l'agenda.</p>
                  {rdv.cancel_reason && <p className="text-xs text-neutral-500">Motif : {rdv.cancel_reason}</p>}
                  <button data-testid="rdv-delete-cancelled-btn" onClick={async () => {
                    if (!window.confirm("Retirer définitivement ce RDV annulé de la liste ?")) return;
                    await api.delete(`/appointments/${rdv.id}`);
                    toast.success("RDV retiré");
                    onSaved(); onClose();
                  }} className="text-[11px] font-semibold bg-white border border-neutral-300 rounded-md px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">
                    Retirer de la liste
                  </button>
                </div>
              )}
            </div>
          )}

          {rdv?.block_id && (
            <Link to={`/agenda?week=${mondayISO(rdv.date)}&highlight=${rdv.block_id}`} data-testid="rdv-goto-agenda"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline">
              <CalendarRange className="w-3.5 h-3.5" /> Voir dans l'agenda (semaine du RDV)
            </Link>
          )}

          <div className="flex gap-2">
            <Button data-testid="rdv-save" onClick={save} disabled={saving} className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white">
              {saving ? "Enregistrement…" : rdv ? "Enregistrer les modifications" : prospect ? "Fixer le RDV" : "Créer le RDV"}
            </Button>
            {rdv && (
              <Button type="button" variant="outline" data-testid="rdv-report-btn" onClick={() => onOpenReport?.({ ...rdv, ...form })} className="gap-1.5">
                <ClipboardList className="w-4 h-4" /> Compte rendu
              </Button>
            )}
            {rdv && (
              <Button variant="outline" data-testid="rdv-delete" onClick={remove} className="text-red-600 hover:bg-red-50 gap-1.5">
                <Trash2 className="w-4 h-4" /> Supprimer
              </Button>
            )}
          </div>
          <p className="text-[11px] text-neutral-400">Un WhatsApp de confirmation est envoyé au contact si le téléphone est renseigné (Twilio requis). Un RDV peut chevaucher d'autres blocs.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
